import { type MusicTheme, THEME_VARIATIONS, trackUrl } from './manifest';
import {
  applyMusicPlaybackRate,
  MUSIC_DEFAULT_VOLUME,
  MUSIC_DUCK_DOWN_MS,
  MUSIC_DUCK_UP_MS,
  MUSIC_FADE_IN_MS,
  MUSIC_FADE_OUT_MS,
  musicTargetGain,
} from '@learnwithai/ui-shared';

// Native (Capacitor WKWebView) — where HTMLMediaElement.volume is IGNORED, so an
// unrouted element must be MUTED rather than ever playing at raw file loudness
// (mirrors web-child/MusicPlayer's NEVER-RAW rule).
const isNative = () =>
  typeof window !== 'undefined' &&
  !!(window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor?.isNativePlatform?.();

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

/**
 * Background music player (parent portal) — the ONLY writer of the music level.
 * Levels/duck/fade constants come from ui-shared/musicLevels — the single volume
 * authority shared with web-child (this file once drifted to its own 0.12
 * default vs web-child's 0.15 → audible jump between surfaces; never again).
 *
 * iOS WKWebView IGNORES HTMLMediaElement.volume — setting `audio.volume` does
 * nothing, which broke the on/off toggle (mute = volume 0), the volume slider,
 * AND ducking under narration. So we route playback through a Web Audio GainNode
 * (whose gain DOES take effect on iOS) and control everything via gain. The
 * `.muted` PROPERTY also works on iOS, so we set it too as a belt-and-suspenders
 * on/off. If Web Audio is unavailable (older WebView / test env) we fall back to
 * element `.volume`/`.muted`, which is correct on Android + web.
 *
 * VOLUME-DRIFT hardening (2026-08-09, mirrored from web-child/MusicPlayer):
 * closed-context rebuild instead of the permanent `_webAudioFailed` latch (the
 * latch made one bad resume play every later track UNROUTED at raw file
 * loudness on iOS/Safari), gain preset before play (never a default-1.0 frame),
 * element volume pinned to 1 while routed (gain is the only attenuator), and
 * faded theme transitions instead of hard cuts.
 */
export class MusicPlayer {
  private _audio: HTMLAudioElement | null = null;
  private _theme: MusicTheme = 'main';
  private _volume = MUSIC_DEFAULT_VOLUME;
  private _muted = false;
  private _ducked = false;
  private _varIndex = 0;
  private _lastVar = -1;
  private _started = false;
  // True while the tab/app is hidden or the device is locked/backgrounded. Music
  // must never START or RESUME while this is set; it clears + resumes on the
  // visible/active transition (ported from web-child/MusicPlayer 2026-08-12).
  private _backgrounded = false;

  // Web Audio graph (preferred — gain works on iOS where element.volume does not)
  private _ctx: AudioContext | null = null;
  private _gain: GainNode | null = null;
  private _source: MediaElementAudioSourceNode | null = null;
  // ONLY set when no WebAudio constructor exists at all (jsdom / ancient WebView).
  // A transient graph-attach throw must NOT latch this (raw-loudness drift bug).
  private _webAudioFailed = false;
  // Pending fade-out → swap timer for theme transitions.
  private _pendingSwap: ReturnType<typeof setTimeout> | null = null;

  // Listener references so dispose() can detach everything the constructor
  // attached (a leaked player kept reacting to visibility/tap events after its
  // provider unmounted — two players fighting over the one audio output).
  private _onGestureResume: (() => void) | null = null;
  private _onVisibilityChange: (() => void) | null = null;
  private _capListenerHandle: unknown = null;

  // Fired ONCE, the first time the audio element genuinely begins sounding (the
  // 'playing' event — not a blocked/failed play() promise). The provider uses it
  // to persist the durable "music intent" flag so a fresh SPA/refresh can resume.
  private _onStarted: (() => void) | null;

  constructor(onStarted?: () => void) {
    this._onStarted = onStarted ?? null;
    // iOS starts an AudioContext suspended until a user gesture — resume on the
    // first tap/click (mirrors the music autoplay unlock). Also retry a paused
    // element: the mount-time autoplay is blocked until a gesture, and without
    // this retry music silently never started (web-child had the same bug).
    if (typeof document !== 'undefined') {
      this._onGestureResume = () => {
        if (this._blocked()) return;
        void this._ctx?.resume?.();
        if (this._started && this._audio && this._audio.paused) {
          this._applyGain(0); // re-assert target BEFORE audible output
          this._audio.play().catch(() => {});
        }
      };
      // CAPTURE phase + the full gesture set (pointerdown/touchstart/click/keydown):
      // the mount-time autoplay is blocked until the FIRST user gesture, and a
      // bubble-phase document listener can be eaten by a React onClick that calls
      // stopPropagation() → the first tap never retried play(), so music only began
      // after some later surface (Settings) re-asserted it (tester B2: "music
      // doesn't start until I go to Settings and back"). Capture fires before any
      // app handler can stop it. { passive } — we never preventDefault.
      const gestureOpts = { capture: true, passive: true } as AddEventListenerOptions;
      document.addEventListener('pointerdown', this._onGestureResume, gestureOpts);
      document.addEventListener('touchstart', this._onGestureResume, gestureOpts);
      document.addEventListener('click', this._onGestureResume, gestureOpts);
      document.addEventListener('keydown', this._onGestureResume, gestureOpts);
    }
    // Lifecycle (ported from web-child): explicit pause on hidden/locked, explicit
    // ensurePlaying() on the visible/foreground transition, and clear a stale duck
    // the lock left behind (narration's un-ducking 'end' event never fires).
    if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
      this._onVisibilityChange = () => {
        if (document.visibilityState === 'hidden') {
          this._backgrounded = true;
          try { this._audio?.pause(); } catch { /* ignore */ }
          this._ducked = false;
        } else {
          this._backgrounded = false;
          this.ensurePlaying();
        }
      };
      document.addEventListener('visibilitychange', this._onVisibilityChange);
    }
    // Native belt-and-suspenders via the global Capacitor App plugin (no static
    // @capacitor import). appStateChange fires active/inactive on background/
    // foreground even when visibilitychange is unreliable in the WebView.
    const capApp = typeof window !== 'undefined'
      ? (window as unknown as { Capacitor?: { Plugins?: { App?: { addListener?: (e: string, cb: (s: { isActive: boolean }) => void) => unknown } } } }).Capacitor?.Plugins?.App
      : undefined;
    this._capListenerHandle = capApp?.addListener?.('appStateChange', ({ isActive }) => {
      if (!isActive) { this._backgrounded = true; this._ducked = false; try { this._audio?.pause(); } catch { /* ignore */ } }
      else { this._backgrounded = false; this.ensurePlaying(); }
    });
  }

  /** Playback must NOT start/resume right now (hidden tab / locked device). */
  private _blocked(): boolean { return this._backgrounded; }

  /**
   * Full teardown for provider unmount: stop playback AND detach every
   * document/app listener the constructor registered. Safe to call twice.
   */
  dispose(): void {
    this.stop();
    if (typeof document !== 'undefined' && typeof document.removeEventListener === 'function') {
      if (this._onGestureResume) {
        const gestureOpts = { capture: true } as EventListenerOptions;
        document.removeEventListener('pointerdown', this._onGestureResume, gestureOpts);
        document.removeEventListener('touchstart', this._onGestureResume, gestureOpts);
        document.removeEventListener('click', this._onGestureResume, gestureOpts);
        document.removeEventListener('keydown', this._onGestureResume, gestureOpts);
      }
      if (this._onVisibilityChange) document.removeEventListener('visibilitychange', this._onVisibilityChange);
    }
    this._onGestureResume = null;
    this._onVisibilityChange = null;
    // Capacitor addListener returns a handle (or a Promise of one) with .remove().
    const h = this._capListenerHandle as { remove?: () => void; then?: (cb: (v: { remove?: () => void }) => void) => void } | null;
    try {
      if (h && typeof h.then === 'function') h.then((res) => { try { res?.remove?.(); } catch { /* ignore */ } });
      else h?.remove?.();
    } catch { /* ignore */ }
    this._capListenerHandle = null;
    this._teardownGraph();
  }

  setTheme(theme: MusicTheme): void {
    if (theme === this._theme && this._started) return;
    this._theme = theme;
    this._varIndex = this._pickNextVar();
    const alreadyPlaying = this._started;
    this._started = true;
    // Theme TRANSITION while a routed track is audible → fade out, then swap —
    // never a hard cut. The swap timer reads current state when it fires, so
    // rapid re-themes coalesce safely.
    if (this._pendingSwap) { clearTimeout(this._pendingSwap); this._pendingSwap = null; }
    if (alreadyPlaying && this._source && this._audio && !this._audio.paused && !this._blocked()) {
      this._fadeGainTo(0.0001, MUSIC_FADE_OUT_MS);
      this._pendingSwap = setTimeout(() => {
        this._pendingSwap = null;
        this._play();
      }, MUSIC_FADE_OUT_MS);
      return;
    }
    this._play();
  }

  /**
   * Guarantee music is actually sounding — resume a paused element (or rebuild a
   * CLOSED AudioContext: Safari/iOS can kill it after a long lock, and the old
   * element is bound to the dead graph). Re-asserts the authority's target gain
   * BEFORE any audible output. Idempotent; a no-op while playing or blocked.
   * (Ported from web-child/MusicPlayer.)
   */
  ensurePlaying(): void {
    if (this._blocked()) return; // hidden/locked — the visibility handler resumes us
    if (!this._started) { this.setTheme(this._theme); return; }
    if (this._ctx && (this._ctx.state as string) === 'closed') {
      this._teardownGraph();
      this._play();
      return;
    }
    void this._ctx?.resume?.();
    // Re-assert the authority's target BEFORE any audible output — an interrupted
    // duck ramp / stale gain must never be what plays on resume.
    this._applyGain(0);
    if (this._audio && this._audio.paused) this._audio.play().catch(() => {});
  }

  setVolume(v: number): void {
    this._volume = clamp01(v);
    this._applyGain(0);
  }

  setMuted(muted: boolean): void {
    this._muted = muted;
    this._applyGain(0);
  }

  duck(): void {
    if (this._ducked || this._muted) return;
    this._ducked = true;
    this._applyGain(MUSIC_DUCK_DOWN_MS);
  }

  restore(): void {
    if (!this._ducked) return;
    this._ducked = false;
    this._applyGain(MUSIC_DUCK_UP_MS);
  }

  stop(): void {
    if (this._pendingSwap) { clearTimeout(this._pendingSwap); this._pendingSwap = null; }
    this._stopAudio();
    this._started = false;
  }

  /** The effective output level — the single authority formula (ui-shared). */
  private _targetGain(): number {
    return musicTargetGain(this._volume, this._muted, this._ducked);
  }

  /** Ramp the gain node toward an explicit value (fade-out path). */
  private _fadeGainTo(value: number, fadeMs: number): void {
    if (!this._ctx || !this._gain) return;
    try {
      const now = this._ctx.currentTime;
      const g = this._gain.gain;
      g.cancelScheduledValues(now);
      g.setValueAtTime(Math.max(0.0001, g.value || 0.0001), now);
      g.linearRampToValueAtTime(Math.max(0.0001, value), now + fadeMs / 1000);
    } catch { /* ignore — swap timer still runs */ }
  }

  /** Push the current target to the gain node (smooth) and the element fallback. */
  private _applyGain(fadeMs: number): void {
    const target = this._targetGain();
    if (this._ctx && this._gain) {
      try {
        const now = this._ctx.currentTime;
        const g = this._gain.gain;
        g.cancelScheduledValues(now);
        g.setValueAtTime(Math.max(0.0001, g.value || 0.0001), now);
        if (fadeMs > 0) g.linearRampToValueAtTime(Math.max(0.0001, target), now + fadeMs / 1000);
        else g.setValueAtTime(target, now);
      } catch {
        this._gain.gain.value = target;
      }
    }
    // Element level. `.muted` is respected on iOS even though `.volume` is not.
    if (this._audio) {
      if (this._source) {
        // Routed through the gain node → gain is the ONLY attenuator. Pinning the
        // element to full prevents double-attenuation (volume²) on platforms that
        // apply element.volume pre-graph, keeping one deterministic level.
        this._audio.muted = this._muted;
        this._audio.volume = 1;
      } else if (!this._webAudioFailed && isNative()) {
        // NEVER-RAW rule (ported from web-child): WebAudio exists on this platform
        // but THIS element could not be routed (transient graph failure). On iOS
        // element.volume is ignored, so unrouted playback would be RAW full-file
        // loudness. Stay muted instead; the next track start / resume re-attaches.
        this._audio.muted = true;
        this._audio.volume = target; // honored where element.volume works; iOS relies on muted
      } else {
        // No WebAudio at all (jsdom / ancient WebView) — element volume IS the
        // control and is correct on Android + web.
        this._audio.muted = this._muted;
        this._audio.volume = target;
      }
    }
  }

  /** Drop a dead/closed WebAudio graph so the next play builds a fresh one. */
  private _teardownGraph(): void {
    if (this._source) { try { this._source.disconnect(); } catch { /* ignore */ } this._source = null; }
    if (this._ctx) { try { void this._ctx.close?.(); } catch { /* ignore */ } }
    this._ctx = null;
    this._gain = null;
  }

  /** Wire the element through ctx → gain → destination. Best-effort; on failure
   *  retries ONCE with a brand-new context (a closed/dead ctx is the common
   *  cause after Safari suspends the page). Gain is preset to the CURRENT
   *  target before it can carry audio — never a default-1.0 frame. */
  private _ensureGraph(audio: HTMLAudioElement): void {
    if (this._webAudioFailed) return;
    const Ctx = typeof window !== 'undefined'
      ? ((window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext })
        .AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext)
      : undefined;
    if (!Ctx) { this._webAudioFailed = true; return; } // no WebAudio — element fallback is correct
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        if (this._ctx && (this._ctx.state as string) === 'closed') this._teardownGraph();
        if (!this._ctx || !this._gain) {
          this._ctx = new Ctx();
          this._gain = this._ctx.createGain();
          this._gain.gain.value = this._targetGain(); // preset — never the 1.0 default
          this._gain.connect(this._ctx.destination);
        }
        void this._ctx.resume?.();
        this._source = this._ctx.createMediaElementSource(audio);
        this._source.connect(this._gain);
        return;
      } catch {
        // Transient failure — tear down and retry once with a fresh context.
        // Never latch _webAudioFailed here (the permanent latch caused the
        // raw-loudness drift on WebKit, where element.volume is ignored).
        this._teardownGraph();
      }
    }
    this._source = null; // unrouted this track — element fallback level applies
  }

  private _stopAudio(): void {
    if (this._source) { try { this._source.disconnect(); } catch { /* ignore */ } this._source = null; }
    if (this._audio) {
      this._audio.onended = null;
      this._audio.onerror = null;
      try { this._audio.pause(); } catch { /* ignore */ }
      this._audio = null;
    }
  }

  private _play(): void {
    // Never START a track while hidden/locked — retry shortly after; the
    // visibility/appState handler also resumes on the foreground transition.
    if (this._blocked()) {
      setTimeout(() => { if (this._started && !this._blocked()) this._play(); }, 500);
      return;
    }
    this._stopAudio();
    const filename = THEME_VARIATIONS[this._theme][this._varIndex];
    const url = trackUrl(this._theme, filename);
    const audio = new Audio(url);
    audio.preload = 'auto';
    // Relaxed tempo: shared MUSIC_PLAYBACK_RATE (0.9, pitch-preserved — owner
    // 2026-08-12 "slow down the music a bit"). MUSIC ONLY, never speech.
    applyMusicPlaybackRate(audio);
    this._audio = audio;
    this._ensureGraph(audio);
    if (this._source && this._gain && this._ctx) {
      // Routed: start from silence and FADE IN to the target — no full-volume
      // (or default-1.0) first frame.
      try {
        const g = this._gain.gain;
        g.cancelScheduledValues(this._ctx.currentTime);
        g.setValueAtTime(0.0001, this._ctx.currentTime);
      } catch { /* ignore */ }
      this._applyGain(MUSIC_FADE_IN_MS);
    } else {
      this._applyGain(0);
    }

    audio.onended = () => {
      if (this._audio !== audio) return;
      this._lastVar = this._varIndex;
      this._varIndex = this._pickNextVar();
      this._play();
    };

    // File missing (audio not yet added) — wait 10 s then retry same variation
    audio.onerror = () => {
      if (this._audio !== audio) return;
      setTimeout(() => { if (this._audio === audio) this._play(); }, 10_000);
    };

    // REAL-PLAY confirmation: the 'playing' event fires only when sound actually
    // begins (never on a blocked/failed autoplay). Persist "music intent" here so
    // the provider records a true-positive only — a blocked play() must not set it.
    const onPlaying = () => {
      if (this._audio !== audio) return;
      try { this._onStarted?.(); } catch { /* ignore */ }
    };
    try { audio.addEventListener?.('playing', onPlaying); } catch { /* jsdom mock — ignore */ }

    audio.play().catch(() => {
      // Autoplay blocked (iOS before first user gesture) — AudioUnlockService
      // registers a one-time unlock listener; music will start on next tap.
    });
  }

  private _pickNextVar(): number {
    const count = THEME_VARIATIONS[this._theme].length;
    if (count === 1) return 0;
    let next: number;
    do { next = Math.floor(Math.random() * count); } while (next === this._lastVar);
    return next;
  }
}
